import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useProgress } from '@/contexts/ProgressContext';
import Pressable from '@/components/base/Pressable';

interface LearningModeProps {
  subject: string;
  topic: string;
  questionCount: number;
  onClose: () => void;
}

type LearningTab = 'recap' | 'quiz' | 'tricks' | 'mistakes' | 'practice';

interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

const sampleQuizzes: Record<string, QuizQuestion[]> = {
  Mathematics: [
    {
      question: 'What is the antiderivative of 3x²?',
      options: ['x³', '3x³', 'x³ + C', '3x³ + C'],
      correctIndex: 2,
      explanation: 'Using the power rule: ∫3x² dx = 3 · x³/3 + C = x³ + C',
    },
    {
      question: '∫ sin(2x) dx equals:',
      options: ['−½ cos(2x) + C', '−cos(2x) + C', '½ cos(2x) + C', 'cos(2x) + C'],
      correctIndex: 0,
      explanation: 'With u = 2x, du = 2dx, so dx = du/2. Then ½∫sin(u)du = −½cos(u) = −½cos(2x) + C',
    },
  ],
  default: [
    {
      question: 'What is the main concept covered in this lesson?',
      options: ['Integration techniques', 'Differentiation rules', 'Algebraic manipulation', 'Graph plotting'],
      correctIndex: 0,
      explanation: 'The lesson focuses on definite integrals and integration techniques like the power rule and u-substitution.',
    },
    {
      question: 'When should you use u-substitution?',
      options: ['For all integrals', 'When there is a composite function', 'Only for trigonometric functions', 'Never needed'],
      correctIndex: 1,
      explanation: 'U-substitution is used when integrating composite functions where the inner function has a derivative present.',
    },
  ],
};

const memoryTricks = [
  { emoji: '🧠', title: 'Power Rule Memory', text: 'Think "add one, divide by the new one" — when integrating xⁿ, add 1 to the power, then divide by that new power.' },
  { emoji: '🔢', title: 'Constant Rule', text: 'A constant inside the integral comes outside unchanged. ∫3f(x)dx = 3∫f(x)dx' },
  { emoji: '🎯', title: 'Bound Check', text: 'Always evaluate the antiderivative at the UPPER bound minus the LOWER bound. F(b) − F(a).' },
  { emoji: '🔄', title: 'U-Sub Pattern', text: 'Look for an inner function whose derivative also appears in the integrand. That is your u.' },
];

const commonMistakes = [
  { emoji: '⚠️', title: 'Forgetting +C', text: 'Indefinite integrals ALWAYS need +C. Definite integrals do not, because the constants cancel out.' },
  { emoji: '⚠️', title: 'Wrong Bound Handling', text: 'After u-substitution, remember to change your bounds to u-values OR convert back to x before evaluating.' },
  { emoji: '⚠️', title: 'Missing du/dx', text: 'When substituting u = g(x), do not forget that dx = du/g\'(x). The derivative must be accounted for.' },
  { emoji: '⚠️', title: 'Power Rule on 1/x', text: 'The power rule does NOT work for x⁻¹. ∫(1/x)dx = ln|x| + C, not x⁰/0.' },
];

const practiceQuestions = [
  '∫₀³ (2x + 1) dx',
  '∫₁² (x³ − x) dx',
  '∫₀^(π/2) cos(3x) dx',
];

export default function LearningMode({ subject, topic, questionCount, onClose }: LearningModeProps) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { recordQuizCompleted } = useProgress();

  const [activeTab, setActiveTab] = useState<LearningTab>('recap');
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizFinished, setQuizFinished] = useState(false);

  const quizzes = sampleQuizzes[subject] || sampleQuizzes.default;
  const currentQuiz = quizzes[quizIndex];

  const t = {
    bg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    cardBg: isDark ? 'bg-[#1C1D21]' : 'bg-neutral-50',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    textPrimary: isDark ? 'text-[#F5F5F7]' : 'text-neutral-900',
    textSecondary: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
    textMuted: isDark ? 'text-[#6E6E73]' : 'text-neutral-400',
    tabActive: isDark ? 'bg-white/[0.07] text-[#F5F5F7]' : 'bg-neutral-100 text-neutral-900',
    tabInactive: isDark ? 'text-[#6E6E73] hover:bg-white/[0.03]' : 'text-neutral-400 hover:bg-neutral-50',
    correctBg: isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200',
    wrongBg: isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200',
  };

  const tabs: { id: LearningTab; label: string; icon: string }[] = [
    { id: 'recap', label: 'Recap', icon: 'ri-refresh-line' },
    { id: 'quiz', label: 'Quiz', icon: 'ri-questionnaire-line' },
    { id: 'tricks', label: 'Tricks', icon: 'ri-lightbulb-line' },
    { id: 'mistakes', label: 'Mistakes', icon: 'ri-error-warning-line' },
    { id: 'practice', label: 'Practice', icon: 'ri-pencil-line' },
  ];

  const handleAnswer = (index: number) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    if (index === currentQuiz.correctIndex) {
      setQuizScore((s) => s + 1);
    }
  };

  const handleNextQuiz = () => {
    if (quizIndex < quizzes.length - 1) {
      setQuizIndex((i) => i + 1);
      setSelectedAnswer(null);
    } else {
      setQuizFinished(true);
      const score = Math.round(((quizScore + (selectedAnswer === currentQuiz.correctIndex ? 1 : 0)) / quizzes.length) * 100);
      recordQuizCompleted(subject.toLowerCase().replace(/\s+/g, '-'), subject, score);
    }
  };

  const handleRestartQuiz = () => {
    setQuizIndex(0);
    setSelectedAnswer(null);
    setQuizScore(0);
    setQuizFinished(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 z-50 ${t.bg} flex flex-col`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-6 pb-3">
        <div>
          <h2 className={`text-lg font-bold ${t.textPrimary}`}>Learning Mode</h2>
          <p className={`text-xs ${t.textMuted} mt-0.5`}>{subject} — {topic}</p>
        </div>
        <Pressable
          onClick={onClose}
          className={`w-9 h-9 rounded-xl ${isDark ? 'bg-white/[0.04]' : 'bg-neutral-100'} flex items-center justify-center`}
          scale={0.9}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-close-line ${t.textMuted} text-sm`} />
          </div>
        </Pressable>
      </div>

      {/* Tab Bar */}
      <div className="px-5 pb-3">
        <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-white/[0.03]' : 'bg-neutral-100'} overflow-x-auto`}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedAnswer(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === tab.id ? t.tabActive : t.tabInactive
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`} />
              </div>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 pb-8">
        <AnimatePresence mode="wait">
          {activeTab === 'recap' && (
            <motion.div
              key="recap"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-4 pt-2"
            >
              <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">🎯</span>
                  <h3 className={`text-sm font-bold ${t.textPrimary}`}>Quick Recap</h3>
                </div>
                <p className={`text-sm ${t.textSecondary} leading-relaxed`}>
                  You just solved <strong className={t.textPrimary}>{questionCount} problems</strong> on {topic}. Here is what you practiced:
                </p>
                <ul className={`mt-3 space-y-2`}>
                  {['Power rule for polynomial integration', 'Fundamental theorem of calculus', 'U-substitution for composite functions', 'Evaluating definite integrals at bounds'].map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-2"
                    >
                      <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-emerald-500 text-[10px]" />
                      </div>
                      <span className={`text-sm ${t.textSecondary}`}>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">📊</span>
                  <h3 className={`text-sm font-bold ${t.textPrimary}`}>Your Progress</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className={`h-2 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} overflow-hidden`}>
                      <motion.div
                        className="h-full rounded-full bg-emerald-500"
                        initial={{ width: 0 }}
                        animate={{ width: '42%' }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                  <span className={`text-xs font-bold ${t.textPrimary}`}>42%</span>
                </div>
                <p className={`text-xs ${t.textMuted} mt-2`}>Mastery in {subject}</p>
              </div>
            </motion.div>
          )}

          {activeTab === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-4 pt-2"
            >
              {!quizFinished ? (
                <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-[11px] font-bold ${t.textMuted}`}>QUESTION {quizIndex + 1}/{quizzes.length}</span>
                    <span className={`text-[11px] ${t.textMuted}`}>{quizScore} correct so far</span>
                  </div>
                  <p className={`text-sm font-semibold ${t.textPrimary} mb-4`}>{currentQuiz.question}</p>
                  <div className="space-y-2">
                    {currentQuiz.options.map((opt, i) => {
                      const isSelected = selectedAnswer === i;
                      const isCorrect = i === currentQuiz.correctIndex;
                      const showCorrect = selectedAnswer !== null && isCorrect;
                      const showWrong = selectedAnswer !== null && isSelected && !isCorrect;
                      return (
                        <motion.button
                          key={i}
                          onClick={() => handleAnswer(i)}
                          whileTap={selectedAnswer === null ? { scale: 0.97 } : undefined}
                          className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors cursor-pointer ${
                            showCorrect
                              ? t.correctBg + ' text-emerald-600 dark:text-emerald-400'
                              : showWrong
                                ? t.wrongBg + ' text-red-600 dark:text-red-400'
                                : isSelected
                                  ? isDark ? 'bg-white/[0.06] border-white/[0.10]' : 'bg-neutral-100 border-neutral-200'
                                  : isDark ? 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.04]' : 'bg-white border-neutral-100 hover:bg-neutral-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-bold ${t.textMuted}`}>{String.fromCharCode(65 + i)}.</span>
                            <span className={showCorrect || showWrong ? 'font-semibold' : ''}>{opt}</span>
                            {showCorrect && (
                              <div className="ml-auto w-5 h-5 flex items-center justify-center">
                                <i className="ri-check-line text-emerald-500" />
                              </div>
                            )}
                            {showWrong && (
                              <div className="ml-auto w-5 h-5 flex items-center justify-center">
                                <i className="ri-close-line text-red-500" />
                              </div>
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                  {selectedAnswer !== null && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-4 p-3 rounded-xl border ${selectedAnswer === currentQuiz.correctIndex ? t.correctBg : t.wrongBg}`}
                    >
                      <p className={`text-xs ${selectedAnswer === currentQuiz.correctIndex ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} font-semibold mb-1`}>
                        {selectedAnswer === currentQuiz.correctIndex ? 'Correct!' : 'Not quite'}
                      </p>
                      <p className={`text-xs ${t.textSecondary}`}>{currentQuiz.explanation}</p>
                    </motion.div>
                  )}

                  {selectedAnswer !== null && (
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={handleNextQuiz}
                      className="w-full mt-4 bg-neutral-900 text-white py-3 rounded-2xl text-sm font-semibold cursor-pointer"
                    >
                      {quizIndex < quizzes.length - 1 ? 'Next Question' : 'Finish Quiz'}
                    </motion.button>
                  )}
                </div>
              ) : (
                <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5 text-center`}>
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-trophy-line text-emerald-500 text-2xl" />
                  </div>
                  <p className={`text-2xl font-bold ${t.textPrimary} mb-1`}>{Math.round((quizScore / quizzes.length) * 100)}%</p>
                  <p className={`text-sm ${t.textSecondary} mb-4`}>{quizScore}/{quizzes.length} correct</p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleRestartQuiz}
                      className={`flex-1 py-3 rounded-2xl text-sm font-semibold border ${t.cardBorder} ${isDark ? 'bg-white/[0.03]' : 'bg-white'} ${t.textPrimary} cursor-pointer`}
                    >
                      Try Again
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-neutral-900 text-white cursor-pointer"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'tricks' && (
            <motion.div
              key="tricks"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-3 pt-2"
            >
              {memoryTricks.map((trick, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-4`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{trick.emoji}</span>
                    <div>
                      <p className={`text-sm font-bold ${t.textPrimary} mb-1`}>{trick.title}</p>
                      <p className={`text-xs ${t.textSecondary} leading-relaxed`}>{trick.text}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === 'mistakes' && (
            <motion.div
              key="mistakes"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-3 pt-2"
            >
              {commonMistakes.map((mistake, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`rounded-2xl border ${t.cardBorder} ${isDark ? 'bg-red-500/[0.02]' : 'bg-red-50/30'} p-4`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{mistake.emoji}</span>
                    <div>
                      <p className={`text-sm font-bold ${isDark ? 'text-red-300' : 'text-red-700'} mb-1`}>{mistake.title}</p>
                      <p className={`text-xs ${t.textSecondary} leading-relaxed`}>{mistake.text}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === 'practice' && (
            <motion.div
              key="practice"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="space-y-4 pt-2"
            >
              <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-base">✏️</span>
                  <h3 className={`text-sm font-bold ${t.textPrimary}`}>Try These</h3>
                </div>
                <div className="space-y-3">
                  {practiceQuestions.map((q, i) => (
                    <Pressable
                      key={i}
                      onClick={() => navigate('/ai-assistant')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border ${isDark ? 'border-white/[0.04] bg-white/[0.02]' : 'border-neutral-100 bg-neutral-50'} transition-colors`}
                      scale={0.97}
                    >
                      <div className={`w-7 h-7 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-neutral-100'} flex items-center justify-center`}>
                        <span className={`text-[10px] font-bold ${t.textMuted}`}>{i + 1}</span>
                      </div>
                      <p className={`text-sm font-mono ${t.textSecondary}`}>{q}</p>
                      <div className="ml-auto w-5 h-5 flex items-center justify-center">
                        <i className={`ri-arrow-right-s-line ${t.textMuted} text-sm`} />
                      </div>
                    </Pressable>
                  ))}
                </div>
              </div>
              <Pressable
                onClick={() => navigate('/ai-assistant')}
                className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2"
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bard-line text-sm" />
                </div>
                <span className="whitespace-nowrap">Ask AI for More Practice</span>
              </Pressable>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}